import { useState, useEffect, useCallback } from 'react';
import { safetyIncidentsService } from '../services/safetyIncidents';
import type {
  SafetyIncidentStats,
  SafetyIncidentItem,
  SafetyIncidentListParams,
} from '../types/safetyIncidents';

// ============================================================================
// useSafetyIncidentStats
// ============================================================================

interface UseSafetyIncidentStatsReturn {
  data: SafetyIncidentStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSafetyIncidentStats(): UseSafetyIncidentStatsReturn {
  const [state, setState] = useState<{
    data: SafetyIncidentStats | null;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await safetyIncidentsService.getStats();
      setState({ data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch safety incident stats';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useSafetyIncidents — paginated list
// ============================================================================

interface UseSafetyIncidentsReturn {
  items: SafetyIncidentItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function useSafetyIncidents(params: SafetyIncidentListParams): UseSafetyIncidentsReturn {
  const limit = params.limit || 20;
  const currentPage = params.page || 1;

  const [state, setState] = useState<{
    items: SafetyIncidentItem[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await safetyIncidentsService.getList(params);
      setState({
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch safety incidents';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.status, params.category, params.assignedToAdminId, params.startDate, params.endDate, params.page, params.limit]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const totalPages = Math.max(1, Math.ceil(state.total / limit));

  return { ...state, page: currentPage, totalPages, refetch: fetch };
}
